import { AlertCircle, Calendar, Globe, Mail, MapPin, User } from "lucide-react";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Controller, FieldErrors, useForm } from "react-hook-form";
import { useOneClickLoginCode } from "../context/OneClickLoginCodeContext";
import { scrollToElement } from "../hooks/useScrollManagement";
import { formatPostalCode } from "./../utils/countryUtils";

import { zodResolver } from "@hookform/resolvers/zod";
import { isValid, parse } from "date-fns";
import { useAnalytics } from "../hooks/useAnalytics";
import { useContactForm } from "../hooks/useContactForm";
import {
  TrialSiteWithConsent,
  useNearestTrialSite,
} from "../hooks/useNearestTrialSite";
import { useConditionalTrialSelection } from "../hooks/useConditionalTrialSelection";
import { ConsentBodyText } from "./ConsentBodyText";
import { TrialIdConfig } from "../types/landingPage";
import { fixDateFormatting } from "../utils/dateUtils";
import { useFieldErrorTracking } from "../utils/trackingUtils";
import {
  getCountryByCode,
  getUSStateFromZipCode,
  isUnderEighteen,
} from "../utils/validationUtils";
import { getSavedZipcode } from "../utils/zipcodeStorage";
import { getMatchingTrialId } from "./contact-form/api/preparePayload";
import {
  ContactFormSchema,
  ExistingPatientContactFormSchema,
  existingPatientContactFormSchema,
  newPatientContactFormSchema,
  NewPatientContactFormSchema,
} from "./contact-form/contactFormSchema";
import {
  CanadianEthnicitySelector,
  EthnicitySelector,
} from "./contact-form/EthnicitySelector";
import { EmrAcceptance } from "./contact-form/EmrAcceptance";
import { AddressSelector } from "./contact-form/AddressSelector";
import {
  EmailUsageModal,
  PhoneUsageModal,
} from "./contact-form/DataUsageModals";
import { ContactFormStatusMessage } from "./contact-form/FormStatusMessage";
import { TermsAcceptance } from "./contact-form/TermsAcceptance";
import { QuestionnaireAnswers } from "./contact-form/utils/questionnaireStorage";
import { PhoneNumberInput } from "./PhoneNumberInput";
import { Modal } from "./ui/Modal";
import { useFeatureFlag } from "../hooks/useFeatureFlags";
import { FEATURE_FLAGS } from "../utils/configCatUtils";

interface ContactFormProps {
  questionnaireAnswers?: QuestionnaireAnswers;
  landingPageId: string;
  countryCode: string;
  trialIdConfig?: string | TrialIdConfig;
  submitButtonText?: string;
  requireEmrConsent?: boolean;
}

/**
 * Contact form component for collecting user information
 */
export function ContactForm({
  questionnaireAnswers = {},
  landingPageId,
  countryCode,
  trialIdConfig,
  submitButtonText = "Submit study application",
  requireEmrConsent = false,
}: ContactFormProps) {
  const selectedCountry = getCountryByCode(countryCode);
  const { oneClickLoginCode } = useOneClickLoginCode();
  const isNewPatient = !oneClickLoginCode;
  const isDeferredEmrConsent = useFeatureFlag(
    FEATURE_FLAGS.EMR_DEFERRED_CONSENT,
    false,
  );
  const {
    isSubmitting,
    isSubmitted,
    showAgeModal,
    isZipCodeValid,
    formattedErrorMessage,
    submissionError,
    handleSubmit: onSubmit,
    handleAgeModalContinue,
    handleAgeModalClose,
    checkZipCodeAndSetEligibility,
  } = useContactForm({
    questionnaireAnswers,
    landingPageId,
    selectedCountry,
    isDeferredEmrConsent,
  });

  // Pre-fill zipcode from prescreening or localStorage
  const savedZipcode = getSavedZipcode();
  const prescreeningZipcode = questionnaireAnswers?.questionAnswers?.zipcode as
    | string
    | undefined;
  const defaultZipcode = prescreeningZipcode || savedZipcode || undefined;

  const {
    register,
    control,
    formState: { errors },
    setValue,
    watch,
    handleSubmit,
  } = useForm<ExistingPatientContactFormSchema | NewPatientContactFormSchema>({
    resolver: zodResolver(
      isNewPatient
        ? newPatientContactFormSchema
        : existingPatientContactFormSchema,
    ),
    defaultValues: {
      country: countryCode,
      ethnicity: [],
      termsAccepted: false,
      phoneValid: false,
      emrConsentAccepted: false,
      zipCode: defaultZipcode, // Pre-fill from prescreening or localStorage
    },
  });
  const typedErrors = errors as FieldErrors<NewPatientContactFormSchema>;

  const dateOfBirthValue = watch("dateOfBirth");
  const [validatedDateOfBirth, setValidatedDateOfBirth] = useState<
    string | undefined
  >(undefined);

  // Store contact form DOB in localStorage for debug panel
  useEffect(() => {
    if (import.meta.env.DEV && dateOfBirthValue) {
      try {
        localStorage.setItem("debug_contactFormDOB", dateOfBirthValue);
      } catch (e) {
        // Ignore localStorage errors
      }
    }
  }, [dateOfBirthValue]);

  const postalCodeValue = watch("zipCode");
  const [validatedPostalCode, setValidatedPostalCode] = useState<
    string | undefined
  >(undefined);

  // Store contact form zipcode in localStorage for debug panel
  useEffect(() => {
    if (import.meta.env.DEV && postalCodeValue) {
      try {
        localStorage.setItem("debug_contactFormZipcode", postalCodeValue);
      } catch (e) {
        // Ignore localStorage errors
      }
    }
  }, [postalCodeValue]);

  const emrConsentAcceptedValue = watch("emrConsentAccepted");
  const showEmrInForm = requireEmrConsent && !isDeferredEmrConsent;

  const localeCode = "EN";

  const successRef = useRef<HTMLDivElement>(null);
  const errorMessageRef = useRef<HTMLDivElement>(null);

  const [isPhoneValid, setIsPhoneValid] = useState(false);
  const [isPhoneFocused, setIsPhoneFocused] = useState(false);
  const [sensitiveDataConsentGranted, setSensitiveDataConsentGranted] =
    useState(false);
  const [
    thirdPartyConsentCheckboxVisible,
    setThirdPartyConsentCheckboxVisible,
  ] = useState(false);
  const [validationErrorsModalOpen, setValidationErrorsModalOpen] =
    useState(false);
  const [validationErrors, setValidationErrors] = useState<
    FieldErrors<ContactFormSchema>
  >({});
  const [emailUsageModalOpen, setEmailUsageModalOpen] = useState(false);
  const [phoneUsageModalOpen, setPhoneUsageModalOpen] = useState(false);

  const { track } = useAnalytics();

  const trackFieldError = useFieldErrorTracking();

  const sensitiveDataConsentCountries = ["CA"];
  const sensitiveDataConsentStates = ["CA", "CO", "MD", "NJ", "TX", "NV", "WA"];
  const sensitiveDataConsentRequired =
    sensitiveDataConsentCountries.includes(countryCode) ||
    (countryCode === "US" &&
      sensitiveDataConsentStates.includes(
        getUSStateFromZipCode(validatedPostalCode ?? "") ?? "",
      ));

  useEffect(() => {
    if (sensitiveDataConsentRequired) {
      track("lp.contact_form.sensitive_data_consent.requested", {
        landing_page_id: landingPageId,
      });
    }
  }, [sensitiveDataConsentRequired]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSensitiveDataConsentChange = (checked: boolean) => {
    setSensitiveDataConsentGranted(checked);
    track(
      checked
        ? "lp.contact_form.sensitive_data_consent.checked"
        : "lp.contact_form.sensitive_data_consent.unchecked",
      { landing_page_id: landingPageId },
    );
  };

  // Track when contact form is viewed
  useEffect(() => {
    if (questionnaireAnswers?.prescreeningStatus === "passed") {
      track("match.screen", {
        landing_page_id: landingPageId,
      });
    }

    track("form.screen", {
      landing_page_id: landingPageId,
    });

    track("lp.contact_form.viewed", {
      is_eligible: questionnaireAnswers?.prescreeningStatus === "passed",
      has_prescreener:
        Object.keys(questionnaireAnswers?.questionAnswers || {}).length > 0,
      landing_page_id: landingPageId,
      useContactPageVariantA: false,
      isDeferredEmrConsentTest2: isDeferredEmrConsent,
    });
  }, []); // Only track once on mount

  // Check if this config uses conditional routing (new system)
  const isConditionalRouting =
    trialIdConfig &&
    typeof trialIdConfig === "object" &&
    "conditionalRouting" in trialIdConfig;

  // EXISTING PATH: Use getMatchingTrialId for string and ageCohorts configs
  const matchingTrialId: string | undefined = useMemo(() => {
    // Skip if using conditional routing (handled by new hook below)
    if (isConditionalRouting) return undefined;

    // Always require validated DOB to ensure proper form flow
    // Even for multi-study (string configs), we want users to enter DOB
    // before showing trial site-specific consent
    if (!trialIdConfig || !validatedDateOfBirth) {
      return undefined;
    }

    const result = getMatchingTrialId(
      trialIdConfig,
      questionnaireAnswers,
      validatedDateOfBirth,
      landingPageId,
    );

    return result;
  }, [
    isConditionalRouting,
    landingPageId,
    trialIdConfig,
    validatedDateOfBirth,
    questionnaireAnswers,
  ]);

  const {
    value: closestTrialSite,
    loading: closestTrialSiteLoading,
    error: closestTrialSiteError,
  } = useNearestTrialSite(
    matchingTrialId,
    selectedCountry.code,
    validatedPostalCode,
    localeCode,
  );

  // NEW PATH: Use conditional trial selection for conditionalRouting configs
  const conditionalSelection = useConditionalTrialSelection({
    enabled:
      !!isConditionalRouting && !!validatedDateOfBirth && !!validatedPostalCode,
    conditionalRouting:
      isConditionalRouting &&
      typeof trialIdConfig === "object" &&
      "conditionalRouting" in trialIdConfig
        ? trialIdConfig.conditionalRouting
        : undefined,
    questionnaireAnswers: questionnaireAnswers || {},
    countryCode: selectedCountry.code,
    postalCode: validatedPostalCode,
    localeCode,
    dateOfBirth: validatedDateOfBirth,
  });

  // Merge results from both paths
  const finalTrialId = isConditionalRouting
    ? conditionalSelection.selectedTrialId
    : matchingTrialId;

  const finalTrialSite = isConditionalRouting
    ? conditionalSelection.trialSite
    : closestTrialSite;

  const finalLoading = isConditionalRouting
    ? conditionalSelection.loading
    : closestTrialSiteLoading;

  const finalError = isConditionalRouting
    ? conditionalSelection.error
    : closestTrialSiteError;

  // Debug logging and localStorage storage for conditional routing
  useEffect(() => {
    if (isConditionalRouting && import.meta.env.DEV) {
      console.log(
        "[ConditionalRouting] Selected trial:",
        finalTrialId,
        conditionalSelection.attemptedTrials,
      );
      if (conditionalSelection.isDeprioritizedFallback) {
        console.log("[ConditionalRouting] Using deprioritized fallback site");
      }
      if (conditionalSelection.skippedDeprioritizedSite) {
        console.log(
          "[ConditionalRouting] Skipped deprioritized site:",
          conditionalSelection.skippedDeprioritizedSite,
        );
      }

      // Store debug info for debug panel
      try {
        localStorage.setItem(
          "debug_conditionalRouting",
          JSON.stringify({
            enabled: true,
            eligibleTrials: conditionalSelection.attemptedTrials,
            selectedTrialId: conditionalSelection.selectedTrialId,
            selectedSiteId: conditionalSelection.trialSite?.id,
            isLastTrial: conditionalSelection.isLastTrial,
            loading: conditionalSelection.loading,
            isDeprioritizedFallback:
              conditionalSelection.isDeprioritizedFallback,
            skippedDeprioritizedSite:
              conditionalSelection.skippedDeprioritizedSite,
            answers: {
              bmi: questionnaireAnswers?.bmi, // BMI is stored at root level of questionnaireAnswers
              hypertension: questionnaireAnswers?.questionAnswers?.hypertension,
            },
          }),
        );
      } catch (e) {
        // Ignore localStorage errors
      }
    }
  }, [
    isConditionalRouting,
    finalTrialId,
    conditionalSelection,
    questionnaireAnswers,
  ]);

  const setHiddenFields = (
    trialSiteWithConsent: TrialSiteWithConsent | undefined,
  ) => {
    if (trialSiteWithConsent) {
      setValue("trialSiteId", String(trialSiteWithConsent.id), {
        shouldDirty: true,
      });
      setValue("trialSiteTrialId", trialSiteWithConsent.trial.id, {
        shouldDirty: true,
      });
      setValue("consentLocaleCode", trialSiteWithConsent.consent.localeCode, {
        shouldDirty: true,
      });
      setValue(
        "thirdPartyContactConsentVersion",
        String(trialSiteWithConsent.consent.template.version),
        { shouldDirty: true },
      );
    } else {
      setValue("trialSiteId", "", { shouldDirty: true });
      setValue("trialSiteTrialId", "", { shouldDirty: true });
      setValue("consentLocaleCode", "", { shouldDirty: true });
      setValue("thirdPartyContactConsentVersion", "", { shouldDirty: true });
    }
  };

  // Validate pre-filled zipcode on mount
  useEffect(() => {
    if (defaultZipcode && defaultZipcode.length === 5) {
      checkZipCodeAndSetEligibility(defaultZipcode);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run once on mount

  useEffect(() => {
    if (postalCodeValue && isZipCodeValid) {
      setValidatedPostalCode(postalCodeValue);
    }
  }, [postalCodeValue, isZipCodeValid]);

  useEffect(() => {
    if (finalLoading) {
      setThirdPartyConsentCheckboxVisible(false);
      setHiddenFields(undefined);
    } else if (finalError) {
      setThirdPartyConsentCheckboxVisible(false);
      setHiddenFields(undefined);
    } else if (finalTrialSite) {
      setThirdPartyConsentCheckboxVisible(true);
      setHiddenFields(finalTrialSite);

      // Store trial site ID in localStorage for debug panel visibility
      if (import.meta.env.DEV) {
        try {
          localStorage.setItem(
            "debug_trialSiteId",
            String(finalTrialSite.id),
          );
        } catch (e) {
          // Ignore localStorage errors
        }
      }
    } else {
      setHiddenFields(undefined);
    }
  }, [finalTrialSite, finalLoading, finalError]);

  const submitButtonDisabled =
    isSubmitting ||
    finalLoading ||
    !isPhoneValid ||
    (showEmrInForm && !emrConsentAcceptedValue) ||
    (sensitiveDataConsentRequired && !sensitiveDataConsentGranted);

  // Handle phone validation state changes from PhoneNumberInput component
  const handlePhoneValidationChange = (isValid: boolean) => {
    setIsPhoneValid(isValid);
    setValue("phoneValid", isValid, { shouldValidate: true });
  };

  // Function to handle input focus and scroll the element into view
  const handleInputFocus = (
    e: React.FocusEvent<
      HTMLInputElement | HTMLSelectElement | HTMLButtonElement
    >,
  ) => {
    setTimeout(() => {
      scrollToElement(e.target, { offset: window.innerHeight * 0.3 });
    }, 100);
  };

  // Handle form validation errors (called when submit fails validation)
  const handleFormErrors = (formErrors: FieldErrors<ContactFormSchema>) => {
    setValidationErrors(formErrors);
    setValidationErrorsModalOpen(true);
  };

  // Handle form submission with tracking
  const handleFormSubmit = (data: ContactFormSchema) => {
    track("submit.form", {
      landing_page_id: landingPageId,
    });

    // Track the form submission
    track("lp.contact_form.submit_clicked", {
      form_complete: Object.values(data).every((v) => v !== "" && v !== false),
      has_questionnaire_answers: Object.keys(questionnaireAnswers).length > 0,
      is_eligible: questionnaireAnswers?.prescreeningStatus === "passed",
      landing_page_id: landingPageId,
      is_zip_code_eligible: isZipCodeValid,
      // Only track non-PII data about the form
      form_fields_count: Object.keys(data).length,
      questionnaire_answers_count: Object.keys(
        questionnaireAnswers?.questionAnswers || {},
      ).length,
    });

    onSubmit(data);
  };

  useEffect(() => {
    if (formattedErrorMessage && errorMessageRef.current) {
      setTimeout(() => {
        scrollToElement(errorMessageRef.current!, {
          behavior: "smooth",
          block: "start",
          offset: window.innerHeight * 0.2,
        });
      }, 100);
    }
  }, [formattedErrorMessage]);

  useEffect(() => {
    if (isSubmitted && successRef.current) {
      setTimeout(() => {
        scrollToElement(successRef.current!, {
          behavior: "smooth",
          block: "start",
        });
      }, 100);
    }
  }, [isSubmitted]);

  const handleDateOfBirthChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    onChange: (value: string) => void,
  ) => {
    let value = e.target.value.replace(/[^\d/]/g, "");

    // Auto-format with slashes
    if (value.length === 2 && !value.includes("/")) {
      value = value + "/";
    } else if (value.length === 5 && value.split("/").length === 2) {
      value = value + "/";
    }

    // Keep input length at most 10 chars (MM/DD/YYYY)
    if (value.length <= 10) {
      onChange(value);

      // Run validation when complete date is entered
      if (value.length === 10) {
        const parsedDate = parse(value, "MM/dd/yyyy", new Date());

        // Check if date is valid and within reasonable range
        const isDateValid =
          isValid(parsedDate) &&
          parsedDate.getFullYear() >= 1900 &&
          parsedDate.getFullYear() <= new Date().getFullYear();

        if (isDateValid) {
          setValidatedDateOfBirth(value);
        } else {
          trackFieldError("dateOfBirth", "invalid");
        }
      }
    }
  };

  return (
    <div
      className="bg-white rounded-lg overflow-hidden shadow-sm border border-gray-100"
      data-test-id="contact-form-container"
    >
      <div className="p-6">
        <ContactFormStatusMessage
          status={questionnaireAnswers.prescreeningStatus}
        />

        {/* API errors - show both parent errors and form submission errors */}
        {formattedErrorMessage && (
          <div
            ref={errorMessageRef}
            className="mb-6 p-4 bg-red-50 border border-red-200 rounded-md text-red-700 flex items-start animate-fade-in"
            data-test-id="contact-form-error"
          >
            <AlertCircle className="w-5 h-5 mr-3 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="font-medium">Form submission failed</p>

              <>
                {submissionError?.name === "NetworkError" && (
                  <div className="mt-3 p-2 bg-red-100 rounded text-xs">
                    <p className="font-medium">Network Error</p>
                    <ul className="list-disc ml-4 mt-1 space-y-1">
                      <li>Check your internet connection</li>
                      <li>
                        Ensure you're not using a VPN that might be blocking the
                        request
                      </li>
                      <li>Try refreshing the page and submitting again</li>
                    </ul>
                  </div>
                )}

                {submissionError?.name === "ApiError" &&
                  submissionError.response?.message ===
                    "Duplicate Application" && (
                    <p className="text-sm mt-1">
                      It looks like you've already applied to this study with
                      these details.
                    </p>
                  )}

                {submissionError?.name === "ApiError" &&
                  submissionError.response?.outcome?.requireLogin && (
                    <p className="text-sm mt-1">
                      We need some additional information to process your
                      application. Please fill in the remaining fields and
                      submit again.
                    </p>
                  )}

                {submissionError?.name === "ApiError" &&
                  submissionError.response &&
                  "status" in submissionError.response &&
                  typeof submissionError.response.status === "number" && (
                    <p className="text-xs mt-2">
                      Error code: {submissionError.response.status}
                    </p>
                  )}
              </>
            </div>
          </div>
        )}

        <form
          onSubmit={handleSubmit(handleFormSubmit, handleFormErrors)}
          className="space-y-8"
          data-test-id="contact-form"
        >
          {/* Location Section - hidden for EMR trials (address moves to Contact Information) */}
          {!showEmrInForm && (
            <div
              className="space-y-6 bg-white rounded-xl p-6 shadow-sm border border-gray-100"
              data-test-id="location-section"
            >
              <div className="flex items-center space-x-2 pb-2 border-b border-gray-200">
                <MapPin className="w-5 h-5 text-primary-500" />
                <h3 className="text-lg font-medium text-gray-900">Location</h3>
              </div>
              <div className="pt-2">
                {/* ZIP Code Field */}
                <div className="mb-6">
                  <label
                    htmlFor="zipCode"
                    className="block text-gray-700 font-medium mb-2"
                  >
                    {selectedCountry.postalCodeLabel}{" "}
                    <span className="text-red-500">*</span>
                  </label>
                  <div className="mt-1 relative rounded-md shadow-sm">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Globe className="h-4 w-4 text-gray-400" />
                    </div>
                    <Controller
                      // Zipcode ~ Postal code (Country agnostic but wording has picked a side)
                      name="zipCode"
                      control={control}
                      render={({ field }) => (
                        <input
                          type="tel"
                          inputMode="numeric"
                          id="zipCode"
                          data-test-id="input-zipcode"
                          value={field.value}
                          onChange={(e) => {
                            const formatted = formatPostalCode(
                              selectedCountry,
                              e.target.value,
                            );
                            field.onChange(formatted);
                            if (formatted.length === 5) {
                              checkZipCodeAndSetEligibility(formatted);
                            }
                          }}
                          onBlur={field.onBlur}
                          onFocus={handleInputFocus}
                          placeholder={selectedCountry.postalCodeExample}
                          className={`block w-48 pl-10 border ${
                            errors.zipCode
                              ? "border-red-300 focus:ring-red-500 focus:border-red-500"
                              : "border-gray-300 focus:ring-primary-500 focus:border-primary-500"
                          } rounded-md shadow-sm py-2 px-3 focus:outline-none text-sm transition-colors duration-200`}
                        />
                      )}
                    />
                  </div>
                  {errors.zipCode && (
                    <div className="text-red-500 text-sm mt-1 flex items-center">
                      <AlertCircle className="w-4 h-4 mr-1" />
                      {errors.zipCode.message}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Personal Information Section */}
          <div
            className="space-y-6 bg-white rounded-lg p-6 shadow-sm border border-gray-100"
            data-test-id="personal-info-section"
          >
            <div className="flex items-center space-x-2 pb-2 border-b border-gray-200">
              <User className="w-5 h-5 text-primary-500" />
              <h3 className="text-lg font-medium text-gray-900">
                Personal Information
              </h3>
            </div>
            <div className="space-y-6">
              {/* First and Last Name - in the same row on desktop */}
              {isNewPatient && (
                <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                  <div>
                    <label
                      htmlFor="firstName"
                      className="block text-sm font-medium text-gray-700"
                    >
                      First name <span className="text-red-500">*</span>
                    </label>
                    <div className="mt-1 relative rounded-md shadow-sm">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <User className="h-4 w-4 text-gray-400" />
                      </div>
                      <input
                        type="text"
                        id="firstName"
                        data-test-id="input-firstName"
                        {...register("firstName")}
                        onFocus={handleInputFocus}
                        className={`block w-full pl-10 border ${
                          typedErrors.firstName
                            ? "border-red-300 focus:ring-red-500 focus:border-red-500"
                            : "border-gray-300 focus:ring-primary-500 focus:border-primary-500"
                        } rounded-md shadow-sm py-2 px-3 focus:outline-none text-sm transition-colors duration-200`}
                      />
                    </div>
                    {typedErrors.firstName && (
                      <div className="text-red-500 text-sm mt-1 flex items-center">
                        <AlertCircle className="w-4 h-4 mr-1" />
                        {typedErrors.firstName.message}
                      </div>
                    )}
                  </div>

                  <div>
                    <label
                      htmlFor="lastName"
                      className="block text-sm font-medium text-gray-700"
                    >
                      Last name <span className="text-red-500">*</span>
                    </label>
                    <div className="mt-1 relative rounded-md shadow-sm">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <User className="h-4 w-4 text-gray-400" />
                      </div>
                      <input
                        type="text"
                        id="lastName"
                        data-test-id="input-lastName"
                        {...register("lastName")}
                        onFocus={handleInputFocus}
                        className={`block w-full pl-10 border ${
                          typedErrors.lastName
                            ? "border-red-300 focus:ring-red-500 focus:border-red-500"
                            : "border-gray-300 focus:ring-primary-500 focus:border-primary-500"
                        } rounded-md shadow-sm py-2 px-3 focus:outline-none text-sm transition-colors duration-200`}
                      />
                    </div>
                    {typedErrors.lastName && (
                      <div className="text-red-500 text-sm mt-1 flex items-center">
                        <AlertCircle className="w-4 h-4 mr-1" />
                        {typedErrors.lastName.message}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Date of Birth - single column, not full width */}
              <div>
                <label
                  htmlFor="dateOfBirth"
                  className="block text-sm font-medium text-gray-700"
                >
                  Date of birth <span className="text-red-500">*</span>
                </label>
                <div className="mt-1 relative rounded-md shadow-sm inline-block max-w-xs">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Calendar className="h-4 w-4 text-gray-400" />
                  </div>
                  <Controller
                    name="dateOfBirth"
                    control={control}
                    render={({ field }) => (
                      <input
                        type="text"
                        id="dateOfBirth"
                        data-test-id="input-dateOfBirth"
                        inputMode="numeric"
                        placeholder="MM/DD/YYYY"
                        className={`block w-full pl-10 border ${
                          typedErrors.dateOfBirth
                            ? "border-red-300 focus:ring-red-500 focus:border-red-500"
                            : "border-gray-300 focus:ring-primary-500 focus:border-primary-500"
                        } rounded-md shadow-sm py-2 px-3 focus:outline-none text-sm transition-colors duration-200`}
                        onFocus={handleInputFocus}
                        value={field.value}
                        onChange={(e) =>
                          handleDateOfBirthChange(e, field.onChange)
                        }
                        onBlur={(e) => {
                          field.onBlur();
                          const value = e.target.value;

                          // Validate on blur if there's a value
                          if (value) {
                            // Check if it's a complete date format
                            if (value.length !== 10) {
                              trackFieldError("dateOfBirth", "incomplete");
                              // Set a temporary invalid value to trigger error
                              if (!typedErrors.dateOfBirth) {
                                setValue("dateOfBirth", value, {
                                  shouldValidate: true,
                                  shouldDirty: true,
                                });
                              }
                              return;
                            }

                            // Validate with date-fns
                            const parsedDate = parse(
                              value,
                              "MM/dd/yyyy",
                              new Date(),
                            );
                            const isDateValid =
                              isValid(parsedDate) &&
                              parsedDate.getFullYear() >= 1900 &&
                              parsedDate.getFullYear() <=
                                new Date().getFullYear();

                            if (!isDateValid) {
                              trackFieldError("dateOfBirth", "invalid");
                              // Trigger validation to show error message
                              setValue("dateOfBirth", value, {
                                shouldValidate: true,
                                shouldDirty: true,
                              });
                            }
                          }
                        }}
                      />
                    )}
                  />
                </div>
                <div className="min-h-[3rem] flex flex-col justify-start">
                  {typedErrors.dateOfBirth ? (
                    <div className="text-red-500 text-sm mt-1 flex items-center">
                      <AlertCircle className="w-4 h-4 mr-1" />
                      {typedErrors.dateOfBirth.message ||
                        "Please enter a valid date (MM/DD/YYYY)"}
                    </div>
                  ) : (
                    <>
                      <div className="text-xs mt-1 text-gray-500">
                        Format: MM/DD/YYYY
                      </div>
                      {dateOfBirthValue ? (
                        <div
                          className={`text-xs mt-1 ${
                            isUnderEighteen(dateOfBirthValue)
                              ? "text-amber-600"
                              : "text-green-600"
                          }`}
                        >
                          {fixDateFormatting(dateOfBirthValue) ||
                            (isUnderEighteen(dateOfBirthValue)
                              ? "Under 18 years old - May need parental consent"
                              : "")}
                        </div>
                      ) : null}
                    </>
                  )}
                </div>
              </div>

              {/* Biological Sex - single column */}
              {isNewPatient && (
                <div data-test-id="sex-options">
                  <label className="block text-sm font-medium text-gray-700">
                    Biological sex <span className="text-red-500">*</span>
                  </label>
                  <div className="mt-2 flex space-x-6">
                    <div className="flex items-center">
                      <Controller
                        name="sex"
                        control={control}
                        render={({ field }) => (
                          <input
                            id="sex-male"
                            data-test-id="input-sex-male"
                            type="radio"
                            value="M"
                            checked={field.value === "M"}
                            onChange={() => field.onChange("M")}
                            onFocus={handleInputFocus}
                            className="h-4 w-4 border-gray-300 text-primary-600 focus:ring-primary-500"
                          />
                        )}
                      />
                      <label
                        htmlFor="sex-male"
                        className="ml-2 block text-sm text-gray-700"
                      >
                        Male
                      </label>
                    </div>
                    <div className="flex items-center">
                      <Controller
                        name="sex"
                        control={control}
                        render={({ field }) => (
                          <input
                            id="sex-female"
                            data-test-id="input-sex-female"
                            type="radio"
                            value="F"
                            checked={field.value === "F"}
                            onChange={() => field.onChange("F")}
                            onFocus={handleInputFocus}
                            className="h-4 w-4 border-gray-300 text-primary-600 focus:ring-primary-500"
                          />
                        )}
                      />
                      <label
                        htmlFor="sex-female"
                        className="ml-2 block text-sm text-gray-700"
                      >
                        Female
                      </label>
                    </div>
                  </div>
                  {typedErrors.sex && (
                    <div className="text-red-500 text-sm mt-1 flex items-center">
                      <AlertCircle className="w-4 h-4 mr-1" />
                      {typedErrors.sex.message}
                    </div>
                  )}
                </div>
              )}

              {/* Use the new EthnicitySelector component */}
              {countryCode === "US" ? (
                <EthnicitySelector control={control} errors={typedErrors} />
              ) : (
                <CanadianEthnicitySelector
                  control={control}
                  errors={typedErrors}
                />
              )}
            </div>
          </div>

          {/* Contact Information Section */}
          <div
            className="space-y-6 bg-white rounded-lg p-6 shadow-sm border border-gray-100"
            data-test-id="contact-info-section"
          >
            <div className="flex items-center space-x-2 pb-2 border-b border-gray-200">
              <Mail className="w-5 h-5 text-primary-500" />
              <h3 className="text-lg font-medium text-gray-900">
                Contact Information
              </h3>
            </div>
            <div className="space-y-6">
              {isNewPatient && (
                <div>
                  <label
                    htmlFor="email"
                    className="block text-sm font-medium text-gray-700"
                  >
                    Email <span className="text-red-500">*</span>
                  </label>
                  <div className="mt-1 relative rounded-md shadow-sm">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Mail className="h-4 w-4 text-gray-400" />
                    </div>
                    <input
                      type="email"
                      id="email"
                      data-test-id="input-email"
                      {...register("email")}
                      onFocus={handleInputFocus}
                      className={`block w-full pl-10 border ${
                        typedErrors.email
                          ? "border-red-300 focus:ring-red-500 focus:border-red-500"
                          : "border-gray-300 focus:ring-primary-500 focus:border-primary-500"
                      } rounded-md shadow-sm py-2 px-3 focus:outline-none text-sm transition-colors duration-200`}
                      placeholder="you@example.com"
                    />
                  </div>
                  {typedErrors.email && (
                    <div className="text-red-500 text-sm mt-1 flex items-center">
                      <AlertCircle className="w-4 h-4 mr-1" />
                      {typedErrors.email.message}
                    </div>
                  )}
                  <button
                    type="button"
                    onClick={() => setEmailUsageModalOpen(true)}
                    className="text-xs text-primary-600 hover:text-primary-500 hover:underline mt-1"
                  >
                    How we use your email address
                  </button>
                </div>
              )}

              {/* Phone field - Replaced with PhoneNumberInput */}
              <div>
                <Controller
                  name="phone"
                  control={control}
                  render={({ field }) => (
                    <PhoneNumberInput
                      id="phone"
                      label="Phone"
                      value={field.value}
                      onChange={field.onChange}
                      onBlur={() => {
                        field.onBlur();
                        setIsPhoneFocused(false);
                      }}
                      onValidationChange={handlePhoneValidationChange}
                      name={field.name}
                      selectedCountry={selectedCountry}
                      onFocus={() => setIsPhoneFocused(true)}
                    />
                  )}
                />
                {!isPhoneValid && watch("phone") && !isPhoneFocused && (
                  <div className="text-red-500 text-sm mt-1 flex items-center">
                    <AlertCircle className="w-4 h-4 mr-1" />
                    Please enter a valid phone number
                  </div>
                )}
                <button
                  type="button"
                  onClick={() => setPhoneUsageModalOpen(true)}
                  className="text-xs text-primary-600 hover:text-primary-500 hover:underline mt-1"
                >
                  How we use your phone number
                </button>
              </div>

              {/* Home Address - only shown for EMR trials */}
              {showEmrInForm && (
                <AddressSelector
                  control={control}
                  errors={errors}
                  onFocus={handleInputFocus}
                  onChange={(address: string, zipCode: string) => {
                    setValue("address", address, { shouldDirty: true });
                    setValue("zipCode", zipCode, { shouldDirty: true });
                    const zipCodeValid = checkZipCodeAndSetEligibility(zipCode);
                    track("lp.contact_form.address_entered", {
                      zipCodeValid,
                    });
                  }}
                />
              )}

              {/* Site-Specific Third Party Contact Consent Checkbox */}
              {thirdPartyConsentCheckboxVisible && (
                <div className="relative flex flex-col items-start p-4 rounded-lg bg-gray-50 hover:bg-gray-100 transition-colors duration-200 group">
                  <div className="text-sm leading-6 font-medium">
                    {finalTrialSite?.consent.preamble}
                  </div>
                  <div className="flex flex-row mt-4 items-center">
                    <div className="flex h-5 items-center flex-shrink-0">
                      <Controller
                        name="thirdPartyContactConsentGranted"
                        control={control}
                        render={({ field }) => (
                          <input
                            id="thirdPartyContactConsentGranted"
                            data-test-id="input-thirdPartyContactConsentGranted"
                            type="checkbox"
                            checked={field.value}
                            onChange={(e) => field.onChange(e.target.checked)}
                            onFocus={handleInputFocus}
                            className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500 transition-colors duration-200"
                          />
                        )}
                      />
                    </div>
                    <label
                      htmlFor="thirdPartyContactConsentGranted"
                      className="ml-3 text-sm leading-6 text-gray-700 cursor-pointer"
                    >
                      {finalTrialSite && (
                        <ConsentBodyText
                          text={finalTrialSite.consent.consent}
                          sites={finalTrialSite.consent.sites}
                        />
                      )}{" "}
                      {finalTrialSite?.consent.privacyPolicyUrl &&
                        finalTrialSite.consent.privacyPolicyOrgName && (
                          <>
                            See{" "}
                            <a
                              href={finalTrialSite.consent.privacyPolicyUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-primary-600 hover:text-primary-500"
                            >
                              {finalTrialSite.consent.privacyPolicyOrgName}'s
                              Privacy Policy
                            </a>{" "}
                            for full details.
                          </>
                        )}
                    </label>
                  </div>
                  {typedErrors.thirdPartyContactConsentGranted && (
                    <div className="text-red-500 text-sm mt-1 flex items-center">
                      <AlertCircle className="w-4 h-4 mr-1" />
                      {typedErrors.thirdPartyContactConsentGranted.message}
                    </div>
                  )}
                </div>
              )}

              {/* Terms Acceptance Component */}
              <TermsAcceptance
                countryCode={countryCode}
                control={control}
                errors={errors}
                handleInputFocus={handleInputFocus}
              />

              {/* Sensitive Data Consent - required for Canada and certain US states */}
              {sensitiveDataConsentRequired && (
                <div className="relative flex items-start p-4 rounded-lg bg-gray-50 hover:bg-gray-100 transition-colors duration-200 group">
                  <div className="flex h-5 items-center flex-shrink-0">
                    <input
                      id="sensitiveDataConsentGranted"
                      data-test-id="input-sensitiveDataConsentGranted"
                      type="checkbox"
                      checked={sensitiveDataConsentGranted}
                      onChange={(e) =>
                        handleSensitiveDataConsentChange(e.target.checked)
                      }
                      className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500 transition-colors duration-200"
                    />
                  </div>
                  <label
                    htmlFor="sensitiveDataConsentGranted"
                    className="ml-3 text-sm leading-6 font-medium text-gray-700 group-hover:text-gray-900 transition-colors duration-200 cursor-pointer"
                  >
                    I consent to HealthMatch collecting and using my health
                    information to assess my eligibility for clinical trials.
                  </label>
                </div>
              )}

              {showEmrInForm && (
                <EmrAcceptance
                  control={control}
                  errors={errors}
                  handleInputFocus={handleInputFocus}
                  showPreamble
                />
              )}

              {/* Hidden Fields */}
              <input
                id="phoneValid"
                type="hidden"
                {...register("phoneValid")}
              />
              <input
                id="trialSiteId"
                type="text"
                className="hidden"
                {...register("trialSiteId")}
                value={""}
              />
              <input
                id="consentLocaleCode"
                type="text"
                className="hidden"
                {...register("consentLocaleCode")}
                value={""}
              />
              <input
                id="thirdPartyContactConsentVersion"
                type="text"
                className="hidden"
                {...register("thirdPartyContactConsentVersion")}
                value={""}
              />
            </div>
          </div>

          {/* Submit Button */}
          <div className="pt-4">
            <button
              type="submit"
              data-test-id="contact-form-submit"
              disabled={submitButtonDisabled}
              className={`w-full flex justify-center py-3 px-4 border border-transparent rounded-md shadow-sm text-base font-medium text-white
              ${
                formattedErrorMessage
                  ? "bg-red-600 hover:bg-red-700 focus:ring-red-500"
                  : "bg-primary-600 hover:bg-primary-700 focus:ring-primary-500"
              } focus:outline-none focus:ring-2 focus:ring-offset-2 ${
                submitButtonDisabled ? "opacity-40 cursor-not-allowed" : ""
              }`}
            >
              {isSubmitting ? (
                <div className="flex items-center">
                  <svg
                    className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  Submitting...
                </div>
              ) : formattedErrorMessage ? (
                <div className="flex items-center">
                  <AlertCircle className="w-5 h-5 mr-2" />
                  Try again
                </div>
              ) : (
                submitButtonText
              )}
            </button>
          </div>
        </form>
      </div>

      <Modal
        isOpen={showAgeModal}
        onClose={handleAgeModalClose}
        title="Eligibility Information"
        data-test-id="age-modal"
      >
        <div className="flex items-start mb-4">
          <div className="bg-blue-100 p-2 rounded-full mr-4 mt-1">
            <AlertCircle className="w-6 h-6 text-blue-600" />
          </div>
          <div>
            <p className="text-gray-800 font-medium mb-2">
              This clinical trial is only open to individuals who are 18 years
              of age or older.
            </p>
            <p className="text-gray-600 mb-4">
              We'll redirect you to a questionnaire designed for your age group,
              where you can explore other potential research opportunities that
              may be better suited for you.
            </p>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row justify-end gap-3">
          <button
            type="button"
            data-test-id="age-modal-back"
            onClick={handleAgeModalClose}
            className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            Go Back
          </button>
          <button
            type="button"
            data-test-id="age-modal-continue"
            onClick={handleAgeModalContinue}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
          >
            Find Other Trials
          </button>
        </div>
      </Modal>

      {/* Validation Errors Modal - helps users understand missing/invalid fields */}
      <Modal
        isOpen={validationErrorsModalOpen}
        onClose={() => setValidationErrorsModalOpen(false)}
        title="Please Complete Required Fields"
        headerClassName="px-6 py-4 bg-amber-50 flex justify-between items-center"
      >
        <div className="space-y-4">
          <div className="flex items-start">
            <div className="bg-amber-100 p-2 rounded-full mr-4 mt-1">
              <AlertCircle className="w-6 h-6 text-amber-600" />
            </div>
            <div>
              <p className="text-gray-800 mb-3">
                To continue, please review and complete the following:
              </p>
              <ul className="list-disc ml-4 space-y-2 text-sm text-gray-600">
                {Object.entries(validationErrors).map(([field, error]) => {
                  // User-friendly labels for form fields
                  const fieldLabels: Record<string, string> = {
                    firstName: "First name",
                    lastName: "Last name",
                    email: "Email address",
                    phone: "Phone number",
                    zipCode: "ZIP code",
                    dateOfBirth: "Date of birth",
                    sex: "Sex",
                    address: "Address",
                    consentToContact: "Consent to be contacted",
                    thirdPartyConsent: "Third party consent",
                    termsAccepted: "Terms and conditions",
                    emrConsentAccepted: "Medical records consent",
                  };
                  // Fields where the label is self-explanatory (don't show message)
                  const selfExplanatoryFields = [
                    "termsAccepted",
                    "consentToContact",
                    "thirdPartyConsent",
                    "emrConsentAccepted",
                  ];
                  const label = fieldLabels[field] || field;
                  const showMessage =
                    error?.message && !selfExplanatoryFields.includes(field);
                  return (
                    <li key={field}>
                      <span className="font-medium text-gray-800">{label}</span>
                      {showMessage && (
                        <span className="text-gray-500">
                          {" "}
                          — {error.message}
                        </span>
                      )}
                    </li>
                  );
                })}
              </ul>
            </div>
          </div>

          <div className="flex justify-end pt-2">
            <button
              type="button"
              onClick={() => setValidationErrorsModalOpen(false)}
              className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
            >
              Review Form
            </button>
          </div>
        </div>
      </Modal>

      <EmailUsageModal
        isOpen={emailUsageModalOpen}
        onClose={() => setEmailUsageModalOpen(false)}
      />
      <PhoneUsageModal
        isOpen={phoneUsageModalOpen}
        onClose={() => setPhoneUsageModalOpen(false)}
      />
    </div>
  );
}
